<script setup lang="ts">
import { computed, onBeforeUnmount, watch } from 'vue'
import { useRouter } from 'vue-router'
import { AlertTriangle, ArrowRight, LoaderCircle, RefreshCw, ScanLine, X } from 'lucide-vue-next'
import { useI18n } from 'vue-i18n'
import SessionStartIssuePreview from '@/components/SessionStartIssuePreview.vue'
import LevelOcrFailure from '@/components/LevelOcrFailure.vue'
import { useLiveStore } from '@/stores/live'

const { t } = useI18n()
const emit = defineEmits<{ 'visibility-change': [occupied: boolean] }>()
const router = useRouter()
const live = useLiveStore()
const issue = computed(() => live.startIssue)
const slowProgress = computed(() => !issue.value && live.sessionStartProgress?.phase === 'slow'
  ? live.sessionStartProgress
  : null)
const visible = computed(() => Boolean(issue.value || slowProgress.value))
watch(visible, (showing) => {
  if (showing) emit('visibility-change', true)
}, { immediate: true, flush: 'sync' })
onBeforeUnmount(() => emit('visibility-change', false))
const metricLabel = computed(() => issue.value?.metric ? t(`live.${issue.value.metric}`) : '')
const title = computed(() => {
  if (slowProgress.value) return t(slowProgress.value.slowReason === 'frame' ? 'live.startFrameSlowTitle' : 'live.startIssue.slowWaitingTitle')
  if (issue.value?.reason === 'capture-unavailable') return t('live.startIssue.captureTitle')
  if (issue.value?.reason === 'slow' || issue.value?.reason === 'unresponsive') return t('live.startIssue.slowTitle')
  return t(issue.value?.metric === 'level' ? 'live.startIssue.levelTitle' : 'live.startIssue.expTitle')
})
const reason = computed(() => issue.value
  ? issue.value.metric === 'exp' && issue.value.confirmationFailureReason
    ? t(`live.expConfirmationFailure.${issue.value.confirmationFailureReason}`)
    : issue.value.reason === 'confirming'
    && issue.value.metric === 'exp'
    && issue.value.confirmedSamples === 1
    ? t('live.startIssue.expOneMore')
    : issue.value.reason === 'confirming'
    && issue.value.metric === 'level'
    && typeof issue.value.candidate === 'number'
    ? t('live.startIssue.levelConfirming', { candidate: issue.value.candidate })
    : t(`live.startIssue.reasons.${issue.value.reason}`, {
      metric: metricLabel.value,
      candidate: issue.value.candidate ?? '--',
    })
  : slowProgress.value
    ? slowProgress.value.slowReason === 'frame'
      ? t('live.startFrameSlowHint')
      : slowProgress.value.currentMetric === 'exp' && slowProgress.value.confirmationFailureReason
      ? t(`live.expConfirmationFailure.${slowProgress.value.confirmationFailureReason}`)
      : slowProgress.value.confirmedSamples === 1
      ? slowProgress.value.currentMetric === 'level' && typeof slowProgress.value.candidate === 'number'
        ? t('live.startIssue.levelConfirming', { candidate: slowProgress.value.candidate })
        : t('live.startIssue.expOneMore')
      : t('live.startIssue.slowWaitingReason')
    : '')
const reviewFailureText = computed(() => {
  const failure = issue.value?.reviewFailureReason
    ?? (slowProgress.value?.currentMetric === 'level' ? slowProgress.value.reviewFailureReason : undefined)
  return failure ? t(`live.startIssue.reviewFailure.${failure}`) : ''
})
const levelCompletion = computed(() => {
  if (issue.value) return issue.value.metric === 'level' ? issue.value.completion : undefined
  const progress = slowProgress.value
  const completion = live.rawReading?.diagnostics.metrics?.level?.completion
  return progress?.currentMetric === 'level' && completion && completion.capturedAt >= progress.requestedAt
    ? completion : undefined
})

function adjust(): void {
  const metric = issue.value?.metric
  if (!metric) return
  live.dismissStartIssue()
  void router.push({ name: 'calibration', query: { metric, source: 'start' } })
}

function retry(): void {
  void live.startSession()
}

function reconnect(): void {
  live.dismissStartIssue()
  void live.selectWindow()
}
</script>

<template>
  <el-dialog
    :model-value="visible"
    class="start-issue-dialog"
    modal-class="start-issue-overlay"
    width="31rem"
    align-center
    append-to-body
    destroy-on-close
    lock-scroll
    trap-focus
    :role="slowProgress ? 'dialog' : 'alertdialog'"
    :close-on-click-modal="false"
    :close-on-press-escape="!live.isStarting"
    :show-close="!live.isStarting"
    @close="live.dismissStartIssue"
    @closed="() => { if (!visible) emit('visibility-change', false) }"
  >
    <template #header="{ titleId, titleClass }">
      <div class="issue-heading">
        <span class="issue-icon" :class="{ loading: slowProgress }" aria-hidden="true">
          <LoaderCircle v-if="slowProgress" />
          <AlertTriangle v-else />
        </span>
        <div><span class="issue-eyebrow">{{ t('live.startIssue.eyebrow') }}</span><h2 :id="titleId" :class="titleClass">{{ title }}</h2></div>
      </div>
    </template>

    <p class="issue-reason" role="alert">{{ reason }}</p>
    <LevelOcrFailure :completion="levelCompletion" :retrying="!issue && Boolean(slowProgress)" />
    <p v-if="reviewFailureText" class="review-note" role="status">{{ reviewFailureText }}</p>
    <SessionStartIssuePreview v-if="issue?.roi" :video="live.video" :roi="issue.roi" :text-label="t('calibration.actualTextRange')" :bar-label="t('calibration.actualBarRange')" />
    <div v-if="issue?.rawText" class="ocr-evidence"><span>{{ t('live.startIssue.detectedText') }}</span><code>{{ issue.rawText }}</code></div>
    <p v-if="issue?.recognitionPath" class="recognition-path">{{ t(`live.startIssue.path.${issue.recognitionPath}`) }}</p>
    <p v-if="issue?.metric" class="issue-guidance"><ScanLine aria-hidden="true" /><span>{{ t(`live.startIssue.guidance.${issue.metric}`) }}</span></p>

    <div v-if="slowProgress" class="issue-actions">
      <el-button @click="live.cancelSessionStart"><X aria-hidden="true" />{{ t('live.startIssue.cancel') }}</el-button>
    </div>
    <div v-else class="issue-actions">
      <el-button v-if="issue?.metric" type="primary" :disabled="live.isStarting" @click="adjust"><ScanLine aria-hidden="true" />{{ t(`live.startIssue.adjust.${issue.metric}`) }}</el-button>
      <el-button v-else-if="issue?.reason === 'capture-unavailable'" type="primary" :disabled="live.isStarting" @click="reconnect">{{ t('live.startIssue.chooseWindow') }}<ArrowRight aria-hidden="true" /></el-button>
      <el-button :loading="live.isStarting" :disabled="live.isStarting" @click="retry"><RefreshCw v-if="!live.isStarting" aria-hidden="true" />{{ live.isStarting ? t('live.startConfirming') : t('live.startIssue.retry') }}</el-button>
      <el-button text :disabled="live.isStarting" @click="live.dismissStartIssue">{{ t('live.startIssue.later') }}</el-button>
    </div>
  </el-dialog>
</template>

<style scoped>
:global(.start-issue-overlay.el-overlay) { background: rgb(8 12 11 / 48%); backdrop-filter: blur(0.125rem); }
:global(.start-issue-overlay .el-overlay-dialog) { display: flex; align-items: center; justify-content: center; padding: 0.75rem; }
:global(.start-issue-dialog.el-dialog) { width: min(31rem, calc(100vw - 1.5rem)) !important; max-height: calc(100dvh - 1.5rem); margin: 0; overflow: hidden; border: 0.0625rem solid var(--border-strong); border-radius: var(--radius-card); background: var(--surface-raised); box-shadow: var(--shadow-raised); }
:global(.start-issue-dialog .el-dialog__header) { margin: 0; padding: 1.25rem 1.25rem 0; }
:global(.start-issue-dialog .el-dialog__body) { padding: 0.875rem 1.25rem 1.25rem; overflow-y: auto; }
.issue-heading { display: grid; grid-template-columns: 2.625rem minmax(0, 1fr); align-items: center; gap: 0.75rem; }
.issue-icon { width: 2.625rem; height: 2.625rem; display: grid; place-items: center; border-radius: 0.5rem; color: var(--danger); background: color-mix(in srgb, var(--danger) 10%, var(--surface)); }
.issue-icon svg { width: 1.25rem; height: 1.25rem; }
.issue-icon.loading { color: var(--primary); background: var(--primary-soft); }
.issue-icon.loading svg { animation: issue-loader-spin 1s linear infinite; }
.issue-eyebrow { display: block; color: var(--danger); font-size: var(--font-caption); font-weight: 700; }
.issue-heading h2 { margin: 0.1875rem 0 0; color: var(--text-strong); font-size: 1.125rem; line-height: 1.35; letter-spacing: 0; }
.issue-reason { margin: 0; color: var(--muted-strong); font-size: var(--font-body); line-height: 1.6; }
.review-note { margin: 0.5rem 0 0; color: var(--muted); font-size: var(--font-label); line-height: 1.5; }
.issue-preview { margin-top: 0.875rem; }
.ocr-evidence { margin-top: 0.75rem; padding: 0.625rem 0.75rem; border-radius: 0.375rem; background: var(--surface-subtle); }
.ocr-evidence span { display: block; color: var(--muted); font-size: var(--font-caption); }
.ocr-evidence code { display: block; margin-top: 0.25rem; overflow-wrap: anywhere; color: var(--text-strong); font-size: var(--font-label); white-space: normal; }
.recognition-path { margin: 0.5rem 0 0; color: var(--muted); font-size: var(--font-caption); }
.issue-guidance { margin: 0.75rem 0 0; display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: start; gap: 0.5rem; color: var(--text); font-size: var(--font-label); line-height: 1.5; }
.issue-guidance svg { width: 1rem; height: 1rem; margin-top: 0.1875rem; color: var(--blue); }
.issue-actions { margin-top: 1rem; display: flex; justify-content: flex-end; flex-wrap: wrap; gap: 0.5rem; }
.issue-actions :deep(.el-button) { margin: 0; }
.issue-actions :deep(svg) { width: 1rem; height: 1rem; }
@keyframes issue-loader-spin { to { transform: rotate(360deg); } }
@media (max-width: 31rem) {
  :global(.start-issue-dialog .el-dialog__header) { padding: 1rem 1rem 0; }
  :global(.start-issue-dialog .el-dialog__body) { padding: 0.75rem 1rem 1rem; }
  .issue-actions { display: grid; }
  .issue-actions :deep(.el-button) { width: 100%; }
}
</style>
